import type { Duration } from 'date-fns';
import add from 'date-fns/add';
import { useState } from 'react';
import Cookies from 'js-cookie';

export const DEFAULT_ANNOUNCEMENT_DURATION: Duration = { months: 2 };

/**
 * A cookie that expires after some time
 */
export type TimedCookie = {
  name: string;
  createdDate: Date;
  expires: Date;
};

function cookie(
  name: string,
  createdDate: Date,
  expiresAfter: Duration = DEFAULT_ANNOUNCEMENT_DURATION,
): TimedCookie {
  return {
    name,
    createdDate,
    expires: add(createdDate, expiresAfter),
  };
}

export const persistentCookie = {
  duskBreakersSurvey: cookie(
    // Typo intentionally not fixed.
    // This was released to production like this originally
    'announcement-duskbreakers-survery-2022-09',
    new Date('2022-09-20'),
    { months: 1 },
  ),
  referAFriend: cookie('tooltip-duskbreakers-refer-a-friend-2022-09', new Date('2022-09-28'), {
    months: 2,
  }),
  omnigrowUpdateModal: cookie(
    'modal-duskbreakers-omnigrow-update-2022-11',
    new Date('2022-11-28'),
    {
      months: 1,
    },
  ),
} as const;

export type UseCookie<T> = {
  value: T;
  expired: boolean;
  setCookie(value: T | undefined): void;
  clearCookie(): void;
};

/**
 * A cookie hook representing the on/off value of a cookie.
 * The lack of a cookie is characterized as false.
 */
export type CookieFlag = UseCookie<boolean>;

export function useCookie<T>(cookie: TimedCookie, parser: (data?: string) => T): UseCookie<T> {
  const [value, setValue] = useState(getCookie);

  function getCookie(): T {
    return parser(Cookies.get(cookie.name));
  }

  function clearCookie() {
    Cookies.remove(cookie.name, {
      expires: cookie.expires,
    });
    // the parser should return a default value here
    setValue(parser(undefined));
  }

  function setCookie(value: T) {
    Cookies.set(cookie.name, String(value), {
      expires: cookie.expires,
    });
    setValue(value);
  }

  return {
    value,
    setCookie,
    clearCookie,
    expired: isTimedCookieExpired(cookie),
  };
}

export function isTimedCookieExpired(cookie: TimedCookie) {
  return cookie.expires < new Date();
}
